import React, { useContext, useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import Imgix from 'react-imgix'
import { useHistory } from 'react-router-dom'
import { ShimmerTable } from 'react-shimmer-effects'
import { useErrorService, useUserService } from '@abroad/components'
import classNames from 'classnames'
import API from '../../utils/API'
import { FancyboxPopup } from '../common'
import { titleCase } from '../../utils/utility'
import { mediaModuleTypeObject } from '../../utils/constants'
import { LayoutContext } from '../../utils/contexts'
import { getAllProgramsKeysArray } from '../../utils/utility'
import {
  gtmPlayExperienceAudioAction,
  gtmPlayExperienceVideoAction,
  gtmReadExperienceBlogAction,
  gtmListenLearningResourceTabAction,
  gtmWatchLearningResourceTabAction,
  gtmReadLearningResourceTabAction,
} from './gtmfunctions'

const RESOURCE_TABS = [
  { key: 'listen', label: 'LISTEN' },
  { key: 'watch', label: 'WATCH' },
  { key: 'read', label: 'READ' },
]

function useResponsiveList(breakpoint = 1024) {
  const [isResponsiveList, setIsResponsiveList] = useState(
    window.innerWidth < breakpoint,
  )

  useEffect(() => {
    const handleResize = () => {
      setIsResponsiveList(window.innerWidth < breakpoint)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [breakpoint])

  return isResponsiveList
}

const QuestLearningResourcesDashboard = ({ questId }) => {
  const [activeTab, setActiveTab] = useState('listen')
  const [isLoading, setIsLoading] = useState(true)
  const [videos, setVideos] = useState([])
  const [blogs, setBlogs] = useState([])
  const [audios, setAudios] = useState([])
  const [journeyId, setJourneyId] = useState()
  const Error = useErrorService()
  const { user } = useUserService()
  const history = useHistory()
  const allProgramsKeyArray = getAllProgramsKeysArray()
  const isResponsiveList = useResponsiveList()
  const {
    setMediaObject,
    setShowPlayBar,
    setContextJourneyId,
    setContextJourneyAudioId,
    setIsJourneyModuleAudio,
    setJourneyAudioType,
    isMediaLoading,
    setIsMediaLoading,
  } = useContext(LayoutContext)

  useEffect(() => {
    const fetchResources = async () => {
      if (!questId) return

      setIsLoading(true)
      try {
        const [mediaResponse, journeyResponse] = await Promise.all([
          API.quests.getQuestMedias(questId),
          API.quests.getQuestJourney(questId),
        ])

        setVideos(mediaResponse?.data?.videos || [])
        setBlogs(mediaResponse?.data?.blogs || [])
        const journeyData = journeyResponse?.data
        setJourneyId(journeyData?._id)
        setAudios(journeyData?.audios || [])
      } catch (error) {
        Error.showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchResources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  const getActiveItems = () => {
    if (activeTab === 'watch') return videos
    if (activeTab === 'read') return blogs
    return audios
  }

  const activeItems = getActiveItems()

  const handleRowClick = (item) => {
    const resourceTitle =
      activeTab === 'listen'
        ? titleCase(item?.mediaOrModuleDetail?.title || 'Experience Audio')
        : item?.title || item?.name || 'Untitled'

    if (activeTab === 'listen') {
      gtmPlayExperienceAudioAction(questId, resourceTitle, user?._id)
      playListenAudio(item)
      return
    }

    if (activeTab === 'watch' && item?.url) {
      gtmPlayExperienceVideoAction(questId, resourceTitle, user?._id)
      FancyboxPopup(item.url)
      return
    }

    if (activeTab === 'read') {
      gtmReadExperienceBlogAction(questId, resourceTitle, user?._id)
      if (item?.type === 'link' && item?.blogLink) {
        window.open(item.blogLink, '_blank', 'noopener,noreferrer')
        return
      }
      history.push(`/your-experiences/${item?._id}/blog-detail`)
    }
  }

  const getMedia = async (
    mediaId,
    mediaSubCategoryId,
    isProgramMedia,
    sequenceId,
  ) => {
    if (isMediaLoading) return
    try {
      setIsMediaLoading(true)
      setShowPlayBar(true)
      let mediaData
      if (isProgramMedia) {
        mediaData = await API.programs.getMediaById(
          mediaSubCategoryId,
          mediaId,
          journeyId,
          sequenceId,
        )
        mediaData = mediaData?.data
      } else {
        mediaData = await API.mediaLibrary.getMediaById(
          mediaSubCategoryId,
          mediaId,
          journeyId,
          sequenceId,
        )
        mediaData = mediaData?.data
      }

      if (mediaData) {
        setContextJourneyId(journeyId)
        setContextJourneyAudioId(mediaData?.journey?.sequenceId)
        setIsJourneyModuleAudio(
          mediaData?.journey?.type === mediaModuleTypeObject?.MODULE,
        )
        setJourneyAudioType(mediaData?.journey?.journeyType)
        setMediaObject(mediaData)
      }
      setIsMediaLoading(false)
    } catch (e) {
      // If user needs upgrade-plan, hide player instead of breaking UI
      if (e?.code === 'upgrade_plan') {
        setShowPlayBar(false)
      }
      setIsMediaLoading(false)
      Error.showError(e)
    }
  }

  const getModuleFirstMedia = async (moduleId, sequenceId) => {
    if (isMediaLoading) return
    try {
      setIsMediaLoading(true)
      setShowPlayBar(true)
      const { data } = await API.programs.getModuleFirstMedia(
        moduleId,
        journeyId,
        sequenceId,
      )
      if (data) {
        setContextJourneyId(journeyId)
        setContextJourneyAudioId(data?.journey?.sequenceId)
        setIsJourneyModuleAudio(
          data?.journey?.type === mediaModuleTypeObject?.MODULE,
        )
        setJourneyAudioType(data?.journey?.journeyType)
        setMediaObject(data)
      }
      setIsMediaLoading(false)
    } catch (e) {
      if (e?.code === 'upgrade_plan') {
        setShowPlayBar(false)
      }
      setIsMediaLoading(false)
      Error.showError(e)
    }
  }

  const playListenAudio = async (item) => {
    if (!item || !journeyId) return

    const mediaOrModuleId = item?.mediaOrModuleId
    const subCategoryId = item?.mediaOrModuleDetail?.subCategoryId?._id
    const isProgramMedia = allProgramsKeyArray.includes(
      item?.mediaOrModuleDetail?.categoryId?.key,
    )

    if (item?.type === mediaModuleTypeObject?.MODULE) {
      // Module row: play first media inside that module
      return getModuleFirstMedia(mediaOrModuleId, item?._id)
    }

    return getMedia(mediaOrModuleId, subCategoryId, isProgramMedia, item?._id)
  }

  const truncateCharacters = (text, charLimit = 50) => {
    if (!text) return ''

    const plainText = String(text)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

    if (!plainText) return ''

    if (plainText.length <= charLimit) {
      return plainText
    }

    return `${plainText.slice(0, charLimit).trim()}....`
  }

  const getItemTitle = (item) => {
    if (activeTab === 'listen') {
      return titleCase(item?.mediaOrModuleDetail?.title || 'Untitled')
    }
    return item?.title || item?.name || 'Untitled'
  }

  const getItemSubtitle = (item) => {
    if (activeTab === 'listen') {
      const category =
        item?.type === mediaModuleTypeObject?.MODULE
          ? item?.mediaOrModuleDetail?.parentId?.title
          : item?.mediaOrModuleDetail?.categoryId?.title
      const subCategory =
        item?.type === mediaModuleTypeObject?.MODULE
          ? item?.mediaOrModuleDetail?.subTitle
          : item?.mediaOrModuleDetail?.subCategoryId?.title

      if (!category && !subCategory) return ''
      if (category && subCategory) {
        return `${titleCase(category)} • ${titleCase(subCategory)}`
      }
      return titleCase(category || subCategory)
    }

    if (activeTab === 'watch') {
      return truncateCharacters(
        item?.description || item?.subTitle || item?.shortDescription || '',
        50,
      )
    }

    if (activeTab === 'read') {
      return truncateCharacters(item?.subTitle || item?.description || '', 50)
    }

    return ''
  }

  const getExpert = (item) => {
    if (activeTab === 'listen') {
      return (
        item?.mediaOrModuleDetail?.subCategoryId?.expert ||
        item?.mediaOrModuleDetail?.parentId?.expert ||
        item?.mediaOrModuleDetail?.expert ||
        '-'
      )
    }
    if (activeTab === 'read') {
      return item?.author || '-'
    }
    return item?.expert || item?.author || '-'
  }

  const getLength = (item) => {
    if (activeTab === 'listen') {
      return item?.mediaOrModuleDetail?.duration
        ? `${Math.round(item.mediaOrModuleDetail.duration / 60)} Mins`
        : '-'
    }
    if (activeTab === 'watch') {
      return item?.timeInMinute ? `${item.timeInMinute} Mins` : '-'
    }
    return item?.readTime ? `${item.readTime} Mins` : '-'
  }

  const getThumbnailSrc = (item) => {
    if (activeTab === 'listen' && item?.mediaOrModuleDetail?.imageFilename) {
      return `${process.env.REACT_APP_IMG_SOURCE}/${item.mediaOrModuleDetail.imageFilename}`
    }
    if (activeTab === 'watch' && item?.thumbnail) {
      return `${process.env.REACT_APP_IMG_SOURCE}/videos/${item.thumbnail}`
    }
    if (activeTab === 'read' && item?.image) {
      return `${process.env.REACT_APP_IMG_SOURCE}/blogs/${item.image}`
    }
    return null
  }

  const isFavorite = (item) => {
    if (activeTab === 'listen') {
      return !!item?.mediaOrModuleDetail?.isFavorite
    }
    return !!item?.isFavorite
  }

  const getListenMediaType = (item) => {
    if (item?.type === mediaModuleTypeObject?.MODULE) {
      return mediaModuleTypeObject?.MODULE
    }

    if (
      allProgramsKeyArray.includes(item?.mediaOrModuleDetail?.categoryId?.key)
    ) {
      return mediaModuleTypeObject?.MODULE_MEDIA
    }

    return mediaModuleTypeObject?.MEDIA_LIBRARY_AUDIO
  }

  const getFavoriteRequest = (item, toggleFavoriteValue) => {
    if (activeTab !== 'listen') return null

    const mediaType = getListenMediaType(item)
    const mediaOrModuleId = item?.mediaOrModuleId
    const subCategoryId = item?.mediaOrModuleDetail?.subCategoryId?._id

    if (mediaType === mediaModuleTypeObject?.MODULE) {
      return API.programs.toggleFavorite(
        {
          isFavorite: !toggleFavoriteValue,
        },
        mediaOrModuleId,
      )
    }

    if (mediaType === mediaModuleTypeObject?.MODULE_MEDIA) {
      return API.programs.favoriteModuleAudio(subCategoryId, mediaOrModuleId, {
        isFavorite: !toggleFavoriteValue,
      })
    }

    return API.mediaLibrary.toggleFavorite(
      {
        isFavorite: !toggleFavoriteValue,
      },
      mediaOrModuleId,
    )
  }

  const toggleFavorite = async (item) => {
    if (activeTab !== 'listen') return

    const currentFavorite = !!item?.mediaOrModuleDetail?.isFavorite

    try {
      const { status } = await getFavoriteRequest(item, currentFavorite)

      if (!status) return

      setAudios((previousAudios) =>
        previousAudios.map((audioItem) =>
          audioItem?._id === item?._id
            ? {
                ...audioItem,
                mediaOrModuleDetail: {
                  ...audioItem?.mediaOrModuleDetail,
                  isFavorite: !currentFavorite,
                },
              }
            : audioItem,
        ),
      )
    } catch (error) {
      Error.showError(error)
    }
  }

  const handleTabClick = (tabKey) => {
    if (tabKey === activeTab) return

    setActiveTab(tabKey)

    if (tabKey === 'listen') {
      gtmListenLearningResourceTabAction(user?._id, questId)
    } else if (tabKey === 'watch') {
      gtmWatchLearningResourceTabAction(user?._id, questId)
    } else if (tabKey === 'read') {
      gtmReadLearningResourceTabAction(user?._id, questId)
    }
  }

  return (
    <section className='quest-dashboard-section quest-learning-resources-section mt-32px'>
      <h3 className='s3 mb-24px'>Learning Resources to Prep for Quest</h3>

      <div className='quest-resource-tabs d-flex mb-4'>
        {RESOURCE_TABS.map((tab) => (
          <button
            key={tab.key}
            type='button'
            className={classNames('quest-resource-tab s10a text-uppercase', {
              'quest-resource-tab-active': activeTab === tab.key,
            })}
            onClick={() => handleTabClick(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className='mt-3'>
          <ShimmerTable row={4} col={4} />
        </div>
      ) : activeItems.length === 0 ? (
        <div className='s5 text-center py-4'>
          No resources available at the moment. Please check back later.
        </div>
      ) : isResponsiveList ? (
        <div className='quest-resource-mobile-list'>
          {activeItems.map((item, index) => {
            const thumbnailSrc = getThumbnailSrc(item)
            const subtitle = getItemSubtitle(item)

            return (
              <div
                key={item?._id || index}
                className='quest-resource-mobile-row'
                onClick={() => handleRowClick(item)}>
                {thumbnailSrc ? (
                  <Imgix
                    className='border-radius-5 quest-resource-thumb'
                    imgixParams={{
                      crop: 'edges',
                      fit: 'crop',
                      w: 72,
                      h: 72,
                    }}
                    src={thumbnailSrc}
                    height={48}
                    width={48}
                    alt='resource'
                  />
                ) : (
                  <div className='quest-resource-thumb-placeholder border-radius-5' />
                )}
                <div className='quest-resource-mobile-copy'>
                  <div className='s6 quest-resource-mobile-title'>
                    {getItemTitle(item)}
                  </div>
                  {subtitle && (
                    <div className='s11 mt-1 text-dark-900-5 quest-resource-mobile-subtitle'>
                      {subtitle}
                    </div>
                  )}
                </div>
                <div className='quest-resource-actions'>
                  <span
                    className={classNames(
                      'icon font-16 quest-resource-mobile-bookmark',
                      {
                        'icon-bookmark-gold': isFavorite(item),
                        'icon-bookmark text-saffron-300': !isFavorite(item),
                        'cursor-pointer': activeTab === 'listen',
                      },
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      toggleFavorite(item)
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className='quest-resource-table-wrap'>
          <Table className='custom-table align-middle table-hover quest-resource-table mb-0'>
            <thead>
              <tr className='s10a border-dark'>
                <th className='quest-resource-index-col'>#</th>
                <th className='quest-resource-title-col'>TITLE</th>
                <th className='quest-resource-expert-col'>EXPERT</th>
                <th className='quest-resource-length-col'>LENGTH</th>
                <th className='quest-resource-actions-col'></th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map((item, index) => {
                const thumbnailSrc = getThumbnailSrc(item)
                const subtitle = getItemSubtitle(item)

                return (
                  <tr
                    key={item?._id || index}
                    className='cursor-pointer'
                    onClick={() => handleRowClick(item)}>
                    <td className='quest-resource-index-col hover:td-play-icon'>
                      <span>{index + 1}</span>
                      <span
                        className='icon icon-play font-16 icon-outline-gold text-start'
                        onClick={(e) => {
                          if (activeTab === 'listen') {
                            e.stopPropagation()
                            playListenAudio(item)
                          }
                        }}
                      />
                    </td>
                    <td className='quest-resource-title-col'>
                      <div className='d-flex align-items-center'>
                        {thumbnailSrc ? (
                          <Imgix
                            className='border-radius-5 quest-resource-thumb'
                            imgixParams={{
                              crop: 'edges',
                              fit: 'crop',
                              w: 72,
                              h: 72,
                            }}
                            src={thumbnailSrc}
                            height={48}
                            width={48}
                            alt='resource'
                          />
                        ) : (
                          <div className='quest-resource-thumb-placeholder border-radius-5' />
                        )}
                        <div className='ms-3 media-details'>
                          <div className='s6 custom-ellipsis-text'>
                            {getItemTitle(item)}
                          </div>
                          {subtitle && (
                            <div className='s11 mt-1 text-dark-900-5 custom-ellipsis-text'>
                              {subtitle}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className='quest-resource-expert-col s7'>
                      <span className='quest-resource-cell-text'>
                        {getExpert(item)}
                      </span>
                    </td>
                    <td className='quest-resource-length-col s7'>
                      <span className='quest-resource-cell-text'>
                        {getLength(item)}
                      </span>
                    </td>
                    <td className='quest-resource-actions-col text-end'>
                      <div className='quest-resource-actions justify-content-end'>
                        <span
                          className={classNames('icon font-16', {
                            'icon-bookmark-gold': isFavorite(item),
                            'icon-bookmark text-saffron-300': !isFavorite(item),
                            'cursor-pointer': activeTab === 'listen',
                          })}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleFavorite(item)
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      )}
    </section>
  )
}

export default QuestLearningResourcesDashboard
